import qs from 'qs';

import { Api, ApiHandlers } from './Api';
import {
  Claim,
  ClaimableShipment,
  CreateDorSigningLinkResponse,
  CreateReturnShipmentsPayload,
  GetClaimsOptions,
  GetClaimsResponse,
  GetInvoiceExtractionResponse,
  ImportFileResponse,
  ClaimsCounts,
  PostClaimsImportDORPayload,
  PostClaimsImportInvoicePayload,
  ReturnCarrierService,
  ReturnShipment,
  Shipment,
  SubmitClaimResponse,
  CreatePictureUploadLinkResponse,
} from './types';
import { ClaimType } from '../components/parcel-finder/claims/types';

type TouchpointsAPIOptions = {
  baseUrl: string;
  token: string;
  shopId: number;
  handlers?: ApiHandlers;
};

class TouchpointsApi {
  private api: Api;
  private shopId: number;

  constructor({ baseUrl, token, shopId, handlers }: TouchpointsAPIOptions) {
    this.api = new Api(baseUrl, token, handlers);
    this.shopId = shopId;
  }

  setToken(token: string) {
    this.api.setToken(token);
  }

  postSSOToken(code: string | null) {
    return this.api.post('/sso/token', { code });
  }

  getSSORedirectLink(namespace: string) {
    return this.api.get(`/sso/redirect-link/${namespace}`);
  }

  setShopId(shopId: number) {
    this.shopId = shopId;
  }

  /** @deprecated use explicit shopId argument */
  shopGet<T>(path: string, params?: Record<string, unknown>) {
    return this.api.get<T>(`/shops/${this.shopId}${path}`, params);
  }

  /** @deprecated use explicit shopId argument */
  shopPost<T>(path: string, body?: object) {
    return this.api.post<T>(`/shops/${this.shopId}${path}`, body);
  }

  getUsers(shopId: number) {
    return this.api.get(`/shops/${shopId}/users`);
  }

  getSendingLogicStats(shopId: number) {
    return this.api.get(`/shops/${shopId}/sending-logic-stats`);
  }

  getCarrierCompanies() {
    return this.api.get('/carrier-companies');
  }

  getCarriers() {
    return this.api.get('/carriers');
  }

  getInvoiceExtraction(fileId: number) {
    return this.shopGet<GetInvoiceExtractionResponse>(`/invoice-extraction/${fileId}`);
  }

  parcelFinderSearch(search: string, searchBy: string) {
    return this.shopGet('/parcel-finder', { search, searchBy });
  }

  findClaimableShipments(shopId: number, trackingCode: string) {
    return this.api.get<ClaimableShipment[]>(`/shops/${shopId}/claimable-shipments/${trackingCode}`);
  }

  getShipment(shopId: number, shipmentId: number) {
    return this.api.get(`/shops/${shopId}/shipments/${shipmentId}`);
  }

  getShipments(shopId: number, orderId: number) {
    return this.api.get<Shipment[]>(`/shops/${shopId}/orders/${orderId}/shipments`);
  }

  getOrder(shopId: number, orderId: number) {
    return this.api.get(`/shops/${shopId}/orders/${orderId}`);
  }

  submitClaim(shopId: number, type: string, claim: object) {
    return this.api.post<SubmitClaimResponse>(`/shops/${shopId}/claims/${type}`, claim);
  }

  createClaimDraft(shopId: number, claim: object) {
    return this.api.post<SubmitClaimResponse>(`/shops/${shopId}/claim-drafts`, claim);
  }

  updateClaimDraft(shopId: number, draftId: number, claim: object) {
    return this.api.patch<SubmitClaimResponse>(`/shops/${shopId}/claim-drafts/${draftId}`, claim);
  }

  submitCustomerAction(claimId: number, customerAction: object) {
    return this.shopPost(`/claims/${claimId}/customer-actions`, customerAction);
  }

  // TODO: move shopId into a separated argument
  getClaimsData({ shopId, ...params }: GetClaimsOptions) {
    const queryString = qs.stringify(params, { arrayFormat: 'comma' });

    return this.api.get<GetClaimsResponse>(`/shops/${shopId}/claims?${queryString}`);
  }

  getClaim(shopId: number, claimId: number) {
    return this.api.get<Claim>(`/shops/${shopId}/claims/${claimId}`);
  }

  cancelClaim(claimId: number) {
    return this.shopPost(`/claims/${claimId}/cancel`);
  }

  getClaimsStats() {
    return this.shopGet('/claims-stats');
  }

  getClaimsShopCarriers(shopId: number) {
    return this.api.get(`/shops/${shopId}/claims-carriers`);
  }

  getClaimsShopSettings(shopId: number) {
    return this.api.get(`/shops/${shopId}/claims-settings`);
  }

  getReturnPortals(shopId: number) {
    return this.api.get(`/shops/${shopId}/return-portals`);
  }

  createReturnPortal(shopId: number, data: object) {
    return this.api.post(`/shops/${shopId}/return-portals`, data);
  }

  getReturnPortal(shopId: number, portalHash: string) {
    return this.api.get(`/shops/${shopId}/return-portals/${portalHash}`);
  }

  isEmailAddressVerified(email: string) {
    return this.api.get(`/email-verified?email=${email}`);
  }

  verifyEmailAddress(emailAddress: string) {
    return this.api.post('/verify-email', { emailAddress });
  }

  updateReturnPortal(shopId: number, portalHash: string, data: object) {
    return this.api.patch(`/shops/${shopId}/return-portals/${portalHash}`, data);
  }

  deleteReturnPortal(shopId: number, portalHash: string) {
    return this.api.delete(`/shops/${shopId}/return-portals/${portalHash}`);
  }

  publishReturnPortal(shopId: number, portalHash: string) {
    return this.api.put(`/shops/${shopId}/return-portals/${portalHash}/published`);
  }

  unpublishReturnPortal(shopId: number, portalHash: string) {
    return this.api.delete(`/shops/${shopId}/return-portals/${portalHash}/published`);
  }

  createReturnShipments(shopId: number, data: CreateReturnShipmentsPayload) {
    return this.api.post<ReturnShipment[]>(`/shops/${shopId}/return-shipments`, data);
  }

  sendTestReturnConfirmationEmail(shopId: number, data: object) {
    return this.api.post(`/shops/${shopId}/return-portals/test-email`, data);
  }

  saveBrandedUrl(shopId: number, hash: string, data: object) {
    return this.api.put(`/shops/${shopId}/return-portals/${hash}/branded-url`, data);
  }

  getAnalyticsDashboards(shopId: number) {
    return this.api.get(`/embeddables?shopId=${shopId}`);
  }

  getDashboard(shopId: number, id: string | number) {
    return this.api.get(`/dashboards/${id}`, { shopId });
  }

  getLook(shopId: number, id: string | number) {
    return this.api.get(`/looks/${id}`, { shopId });
  }

  getExplore(shopId: number, id: string | number) {
    return this.api.get(`/explores/${id}`, { shopId });
  }

  permissions(shopId: number) {
    return this.api.get('/permissions', { shopId });
  }

  saveReturnPortalsTranslations(shopId: number, data: object) {
    return this.api.patch(`/shops/${shopId}/return-portals-translations`, data);
  }

  getReturnPortalsTranslations(shopId: number) {
    return this.api.get(`/shops/${shopId}/return-portals-translations`);
  }

  getClaimCustomerResponse(shopId: number, claimId: number) {
    return this.api.get(`/shops/${shopId}/claims/${claimId}/customer-response`);
  }

  createChatbotToken(shopId: number) {
    return this.api.post(`/shops/${shopId}/chatbot/token`, {});
  }

  submitCarrierClaimPayments(data: object) {
    return this.shopPost('/carrier-claim-payments', data);
  }

  getWarehouses(shopId: number) {
    return this.api.get(`/shops/${shopId}/warehouses`);
  }

  getSLAs(shopId: number) {
    return this.api.get(`/shops/${shopId}/sla`);
  }

  postClaimsDeadlines(shopId: number, data: object) {
    return this.api.post(`/shops/${shopId}/claims-deadlines`, data);
  }

  downloadDor(shipmentId: number, language: string) {
    return this.shopGet(`/dors/${shipmentId}/${language}`);
  }

  createDorSigningLink(
    shopId: number,
    shipmentId: number,
    language: string,
    recipientEmail?: string | null
  ): Promise<CreateDorSigningLinkResponse> {
    return this.api.post(`/shops/${shopId}/dor-signing-links`, { shipmentId, language, recipientEmail });
  }

  createPictureUploadLink(
    shopId: number,
    shipmentId: number,
    claimType: ClaimType
  ): Promise<CreatePictureUploadLinkResponse> {
    return this.api.post(`/shops/${shopId}/picture-upload-links`, { shipmentId, claimType });
  }

  postClaimsImportInvoice(shopId: number, data: PostClaimsImportInvoicePayload) {
    return this.api.post<ImportFileResponse>(`/shops/${shopId}/claims-import-invoice`, data);
  }

  postClaimsImportDOR(shopId: number, data: PostClaimsImportDORPayload) {
    return this.api.post<ImportFileResponse>(`/shops/${shopId}/claims-import-dor`, data);
  }

  getReturnCarrierServices(shopId: number, countryCode: string) {
    return this.api.get<ReturnCarrierService>(`/shops/${shopId}/return-carrier-services?countryCode=${countryCode}`);
  }

  getAvailableDorLanguages(shopId: number, carrierSlug: string, isReturnShipment: boolean) {
    return this.api.get<string[]>(`/shops/${shopId}/dors/available-languages/${carrierSlug}`, { isReturnShipment });
  }

  getClaimsCounts(shopId: number) {
    return this.api.get<ClaimsCounts>(`/shops/${shopId}/claims-counts`);
  }
}

export default TouchpointsApi;
